interface CachedFileContent {
  content: string;
  format: string;
  originalFileName: string;
  blob?: Blob;
}

const fileContentCache: Map<string, CachedFileContent> = new Map();

const USE_STREAM_ENDPOINT = true;

export function getCachedFileContent(resourceUid: string): CachedFileContent | null {
  return fileContentCache.get(resourceUid) || null;
}

export function setCachedFileContent(resourceUid: string, data: CachedFileContent): void {
  fileContentCache.set(resourceUid, data);
  console.log(' Cached file content for', resourceUid);
}

export function clearCacheEntry(resourceUid: string): void {
  const cached = fileContentCache.get(resourceUid);
  if (cached?.blob) {
    URL.revokeObjectURL(URL.createObjectURL(cached.blob));
  }
  fileContentCache.delete(resourceUid);
  console.log(' Cleared cache entry for', resourceUid);
}

export function clearAllCache(): void {
  fileContentCache.clear();
  console.log(' Cleared all cache entries');
}

export function hasCachedContent(resourceUid: string): boolean {
  return fileContentCache.has(resourceUid);
}


async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}


export async function getFileContentWithCache(
  resourceUid: string,
  fileName?: string
): Promise<CachedFileContent | null> {
  const cached = fileContentCache.get(resourceUid);
  if (cached) {
    console.log(' Using cached file content for', resourceUid);
    return cached;
  }

  try {
    if (USE_STREAM_ENDPOINT) {
      const { downloadFileStream } = await import('@/store/services/order/fileStreamService');
      const blob = await downloadFileStream(resourceUid);

      if (blob) {
        const base64Content = await blobToBase64(blob);

        const fileData: CachedFileContent = {
          content: base64Content,
          format: blob.type || 'application/pdf',
          originalFileName: fileName || 'document',
          blob: blob
        };

        fileContentCache.set(resourceUid, fileData);
        console.log(' Fetched (stream) and cached file content for', resourceUid);
        return fileData;
      }
    } else {
      const { downloadFile } = await import('@/store/services/offerService');
      const response = await downloadFile(resourceUid);

      if (response && response.content) {
        const fileData: CachedFileContent = {
          content: response.content,
          format: response.format || 'application/pdf',
          originalFileName: fileName || response.originalFileName || 'document'
        };

        fileContentCache.set(resourceUid, fileData);
        console.log(' Fetched (base64) and cached file content for', resourceUid);
        return fileData;
      }
    }
  } catch (error) {
    console.error(' Error fetching file content:', error);

    if (USE_STREAM_ENDPOINT) {
      console.log(' Falling back to base64 endpoint');
      try {
        const { downloadFile } = await import('@/store/services/offerService');
        const response = await downloadFile(resourceUid);

        if (response && response.content) {
          const fileData: CachedFileContent = {
            content: response.content,
            format: response.format || 'application/pdf',
            originalFileName: fileName || response.originalFileName || 'document'
          };

          fileContentCache.set(resourceUid, fileData);
          return fileData;
        }
      } catch (fallbackError) {
        console.error(' Fallback also failed:', fallbackError);
      }
    }
  }

  return null;
}

export function getCachedBlob(resourceUid: string): Blob | null {
  const cached = fileContentCache.get(resourceUid);
  return cached?.blob || null;
}

export async function getFileBlobWithCache(resourceUid: string, fileName?: string): Promise<Blob | null> {
  const cached = fileContentCache.get(resourceUid);
  if (cached?.blob) {
    console.log(' Using cached blob for', resourceUid);
    return cached.blob;
  }

  const fileData = await getFileContentWithCache(resourceUid, fileName);
  return fileData?.blob || null;
}
